/**
 * A deliberately small Markdown subset for the billing flow documents.
 *
 * This produces a token tree, never an HTML string, so the renderer can build
 * React elements and injection is structurally impossible rather than
 * defended against. That matters more than feature coverage here: the Hub is
 * an authenticated internal surface and pulling in a Markdown library to render
 * seven documents we author ourselves would add a dependency and an
 * `dangerouslySetInnerHTML` call for no benefit.
 *
 * Supported, because it is what the documents use: `##`/`###` headings,
 * paragraphs, `-` lists, fenced code, pipe tables, `**bold**` and `` `code` ``.
 * Anything else — including raw HTML — is treated as literal text.
 */

export type Span =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "code"; text: string };

export type Block =
  | { kind: "heading"; level: 2 | 3; spans: Span[] }
  | { kind: "paragraph"; spans: Span[] }
  | { kind: "list"; items: Span[][] }
  | { kind: "code"; text: string }
  | { kind: "table"; headers: Span[][]; rows: Span[][][] };

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

export function parseInline(text: string): Span[] {
  const spans: Span[] = [];
  for (const part of text.split(INLINE)) {
    if (!part) continue;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      spans.push({ kind: "strong", text: part.slice(2, -2) });
    } else if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      spans.push({ kind: "code", text: part.slice(1, -1) });
    } else {
      spans.push({ kind: "text", text: part });
    }
  }
  return spans;
}

function tableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

const DIVIDER = /^\|?[\s:|-]+\|[\s:|-]*$/;

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", spans: parseInline(paragraph.join(" ")) });
      paragraph = [];
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (line.startsWith("```")) {
      flushParagraph();
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        body.push(lines[index]);
        index += 1;
      }
      blocks.push({ kind: "code", text: body.join("\n") });
      continue;
    }

    const heading = /^(#{2,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({
        kind: "heading",
        level: heading[1].length === 2 ? 2 : 3,
        spans: parseInline(heading[2].trim()),
      });
      continue;
    }

    // A table needs its divider row; without one these are ordinary lines.
    if (line.trim().startsWith("|") && DIVIDER.test(lines[index + 1] ?? "")) {
      flushParagraph();
      const headers = tableCells(line).map(parseInline);
      const rows: Span[][][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(tableCells(lines[index]).map(parseInline));
        index += 1;
      }
      index -= 1;
      blocks.push({ kind: "table", headers, rows });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      flushParagraph();
      const items: Span[][] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(parseInline(lines[index].replace(/^\s*[-*]\s+/, "")));
        index += 1;
      }
      index -= 1;
      blocks.push({ kind: "list", items });
      continue;
    }

    if (!line.trim()) {
      flushParagraph();
      continue;
    }
    paragraph.push(line.trim());
  }

  flushParagraph();
  return blocks;
}
